import React, { useEffect, useState } from 'react';

import * as toast from 'utils/toast';
import { fetchByEmail } from 'services/user';
import { getFormattedDate } from 'utils/date';
import LFModal from 'components/common/LFModal';
import { handleError } from 'utils/errorHandler';
import ImportButton from 'components/common/button/ImportButton';
import { UserPayloadInput } from 'interface/createUpdateForm';
import { DateInput } from 'interface/common';

interface ImportValues {
  leaveIssuerEmail?: string;
  teamManagerEmail?: string;
  appraiserEmail?: string;
  coAppraiserEmail?: string;
  designation?: string;
  designationId?: number;
  area?: string;
  areaId?: number;
  engagementStatus?: string;
  engagementStatusId?: number;
  rolesIds?: string;
  joinDate?: DateInput;
  dateofBirth?: DateInput;
}

interface ImportSheetProps {
  setInitialValues: React.Dispatch<React.SetStateAction<UserPayloadInput>>;
}

const ImportSheet: React.FC<ImportSheetProps> = (props) => {
  const { setInitialValues } = props;
  const [sheetValues, setSheetValues] = useState('');

  const [newValue, setNewValue] = useState<ImportValues | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setSheetValues(e.target.value);
  };

  useEffect(() => {
    if (!newValue) {
      return;
    }

    const setEmployeeInDebounce = async (email: keyof ImportValues, id: string) => {
      try {
        const userEmail = newValue[email];
        if (!userEmail || typeof userEmail !== 'string') {
          return;
        }

        const data = (await fetchByEmail(userEmail)) as Array<{ id?: number }>;
        const [firstRecord] = data;

        setInitialValues((prevState) => ({
          ...prevState,
          [id]: firstRecord?.id,
        }));
      } catch (err) {
        handleError(err);
      }
    };

    const { joinDate: importedJoinDate, dateofBirth: importedDateofBirth, ...importRest } = newValue;

    // Import sheet may parse dates as Moment; UserPayloadInput only allows string/Date for joinDate and string for DOB.
    const joinDate: UserPayloadInput['joinDate'] =
      importedJoinDate === undefined
        ? undefined
        : importedJoinDate === null
          ? null
          : getFormattedDate(importedJoinDate);

    const dateofBirth: UserPayloadInput['dateofBirth'] =
      importedDateofBirth === undefined
        ? undefined
        : importedDateofBirth === null
          ? null
          : getFormattedDate(importedDateofBirth);

    setInitialValues((prevState) => ({
      ...prevState,
      ...importRest,
      joinDate,
      dateofBirth,
    }));

    /* Set Leave Issuer */
    setEmployeeInDebounce('leaveIssuerEmail', 'leaveIssuerId');

    /* Set Team Manager */
    setEmployeeInDebounce('teamManagerEmail', 'teamManagerId');

    /* Set Appraiser */
    setEmployeeInDebounce('appraiserEmail', 'appraiserId');

    /* Set CoAppraiser */
    setEmployeeInDebounce('coAppraiserEmail', 'coAppraiserId');

    const updatedValue = {
      ...newValue,
      designationAreaHistory:
        newValue.designation && newValue.designationId && newValue.area && newValue.areaId
          ? [
              {
                area: { id: newValue.areaId, name: newValue.area },
                designation: { id: newValue.designationId, name: newValue.designation },
                transitionDate: getFormattedDate(newValue?.joinDate),
              },
            ]
          : [],
      empStatusHistory:
        newValue.engagementStatus && newValue.engagementStatusId
          ? [
              {
                engagementStatus: { id: newValue.engagementStatusId, name: newValue.engagementStatus },
                transitionDate: getFormattedDate(newValue?.joinDate),
              },
            ]
          : [],
      roleIds: newValue?.rolesIds?.split(',').map(Number),
      joinDate: getFormattedDate(newValue?.joinDate),
      dateofBirth: newValue.dateofBirth ? getFormattedDate(newValue.dateofBirth) : '',
    };

    setInitialValues((prevState) => ({
      ...prevState,
      designationAreaHistory: updatedValue.designationAreaHistory,
      empStatusHistory: updatedValue.empStatusHistory,
      roleIds: updatedValue.roleIds,
      joinDate: updatedValue.joinDate,
      dateofBirth: updatedValue.dateofBirth,
    }));
  }, [newValue, setInitialValues]);

  const handleSubmit = () => {
    try {
      const parsedJson = JSON.parse(sheetValues) as ImportValues;

      setIsModalOpen(false);
      setNewValue(parsedJson);
      setSheetValues('');
    } catch {
      toast.error({
        title: 'Error',
        message: 'Please enter correct JSON data.',
      });
    }
  };

  const handleModalTriggerClick = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSheetValues('');
  };

  const htmlForm = (
    <form>
      <textarea
        name="description"
        value={sheetValues}
        placeholder="Place the JSON data from the excel sheet here"
        className="form-elem"
        onChange={handleChange}
      />
    </form>
  );

  return (
    <div className="import">
      <p className="import__title">Import Leapfrogger</p>
      <p className="import__detail">Import Leapfrogger&apos;s data from excel sheet.</p>

      <LFModal
        TriggerBtn={({ onClick }) => (
          <ImportButton
            onClick={() => {
              handleModalTriggerClick();
              onClick();
            }}
          />
        )}
        title={{ text: 'Import Data From Excel Sheet' }}
        renderFooter={true}
        htmlContent={htmlForm}
        isOpen={isModalOpen}
        buttons={[
          {
            text: 'Confirm',
            className: 'mr-12',
            onClick: handleSubmit,
            showLoading: true,
          },
          {
            text: 'Cancel',
            type: 'outlined-grey',
            className: 'py-8 px-12',
            onClick: handleClose,
            close: true,
          },
        ]}
      />
    </div>
  );
};

export default ImportSheet;
